import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Website {
  id: string;
  clientName: string | null;
  businessOwner: string | null;
  websiteLink: string | null;
  typeOfWebsite: string | null;
  plan: string | null;
  status: string;
  stripePortalUrl: string | null;
  startDate: string | null;
  nextBilling: string | null;
  notes: string | null;
}

interface PortalSession {
  ownerName: string | null;
  email: string;
  websites: Website[];
}

export default function PortalDashboardPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState<PortalSession | null>(null);
  const [loading, setLoading] = useState(true);

  const [cancelModal, setCancelModal] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelledIds, setCancelledIds] = useState<Set<string>>(new Set());
  const [editInfoOpen, setEditInfoOpen] = useState<string | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('portal_session');
    if (!stored) {
      navigate('/portal/login');
      return;
    }
    try {
      const parsed: PortalSession = JSON.parse(stored);
      setSession(parsed);
    } catch {
      navigate('/portal/login');
      return;
    }
    setLoading(false);
  }, [navigate]);

  const handleSignOut = () => {
    sessionStorage.removeItem('portal_session');
    navigate('/portal/login');
  };

  const openCancelModal = (websiteId: string) => {
    setCancelModal(websiteId);
    setCancelReason('');
    setCancelSuccess(false);
  };

  const closeCancelModal = () => {
    setCancelModal(null);
    setCancelReason('');
    setCancelSuccess(false);
  };

  const handleCancelSubmit = async (e: React.FormEvent, website: Website) => {
    e.preventDefault();
    if (!session) return;
    setCancelSubmitting(true);

    await supabase.from('cancellation_requests').insert({
      client_email: session.email,
      client_name: session.ownerName,
      reason: `[${website.websiteLink || 'Website'}] ${cancelReason.trim()}`.trim(),
    });

    setCancelSubmitting(false);
    setCancelSuccess(true);
    setCancelledIds((prev) => new Set([...prev, website.id]));
    setTimeout(() => { closeCancelModal(); }, 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line text-3xl text-[#F5C518] animate-spin"></i>
          <p className="text-sm text-[#999] tracking-wide">Loading your portal...</p>
        </div>
      </div>
    );
  }

  const websites = session?.websites || [];
  const cancellingWebsite = websites.find((w) => w.id === cancelModal) || null;
  const ownerName = session?.ownerName;
  const clientDisplayName = websites[0]?.clientName || ownerName;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-[#F5C518]"></div>
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-10 flex items-center justify-between">
          <div>
            <p className="text-[#F5C518] text-xs tracking-[0.25em] uppercase font-medium mb-1">
              Client Portal
            </p>
            <h1 className="font-display text-white text-2xl sm:text-3xl tracking-wide">
              {clientDisplayName ? `Hi, ${clientDisplayName.toUpperCase()}` : session?.email}
            </h1>
            <p className="text-white/30 text-xs mt-1 tracking-wide">{session?.email}</p>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 text-white/40 hover:text-[#F5C518] text-xs tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-sm"></i>
            Sign Out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 sm:px-10 py-14">

        {/* Section label */}
        <div className="flex items-center gap-4 mb-8">
          <p className="font-display text-xs tracking-[0.25em] text-[#999] uppercase">
            {websites.length === 1 ? 'Your Website' : `Your Websites (${websites.length})`}
          </p>
          <div className="flex-1 h-px bg-[#eee]"></div>
        </div>

        {/* Website Cards */}
        <div className="space-y-8">
          {websites.map((website) => {
            const isCancelled = cancelledIds.has(website.id);
            const websiteLink = website.websiteLink;
            const displayUrl = websiteLink ? websiteLink.replace(/^https?:\/\//, '') : null;
            const fullUrl = websiteLink
              ? websiteLink.startsWith('http') ? websiteLink : `https://${websiteLink}`
              : null;

            return (
              <div key={website.id} className="border border-[#eee] rounded-sm overflow-hidden">
                {/* Card Header */}
                <div className="bg-[#FAFAF8] px-6 py-4 border-b border-[#eee] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#F5C518]/10 rounded-sm">
                      <i className="ri-global-line text-sm text-[#F5C518]"></i>
                    </div>
                    <div>
                      {/* Client name as the card title */}
                      <p className="font-display text-sm tracking-widest text-[#1a1a1a] uppercase">
                        {website.clientName || website.businessOwner || ownerName || 'Website'}
                      </p>
                      {/* Website link below the name */}
                      {fullUrl && (
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#F5C518] hover:underline tracking-wide flex items-center gap-1 mt-0.5"
                        >
                          <i className="ri-external-link-line text-xs"></i>
                          {displayUrl}
                        </a>
                      )}
                    </div>
                  </div>
                  <span
                    className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full capitalize ${
                      isCancelled
                        ? 'text-red-500 bg-red-50'
                        : website.status?.toLowerCase() === 'active'
                        ? 'text-emerald-600 bg-emerald-50'
                        : 'text-amber-600 bg-amber-50'
                    }`}
                  >
                    {isCancelled ? 'Cancellation Requested' : (website.status || 'Active')}
                  </span>
                </div>

                {/* Card Details */}
                <div className="px-6 py-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-6">
                    {/* Business Owner */}
                    <div>
                      <p className="text-xs text-[#999] uppercase tracking-wider mb-1">Business Owner</p>
                      <p className="text-sm text-[#1a1a1a]">{website.businessOwner || ownerName || '—'}</p>
                    </div>

                    {/* Website Link */}
                    <div>
                      <p className="text-xs text-[#999] uppercase tracking-wider mb-1">Website</p>
                      {fullUrl ? (
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#F5C518] hover:underline truncate block"
                        >
                          {displayUrl}
                        </a>
                      ) : (
                        <p className="text-sm text-[#bbb]">—</p>
                      )}
                    </div>

                    {/* Next Billing */}
                    {website.nextBilling && (
                      <div>
                        <p className="text-xs text-[#999] uppercase tracking-wider mb-1">Next Billing</p>
                        <p className="text-sm text-[#1a1a1a]">{website.nextBilling}</p>
                      </div>
                    )}
                  </div>

                  {website.notes && (
                    <div className="mb-6 bg-[#FAFAF8] border border-[#eee] px-4 py-3 rounded-sm">
                      <p className="text-xs text-[#999] uppercase tracking-wider mb-1">Notes</p>
                      <p className="text-sm text-[#555] leading-relaxed">{website.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  {!isCancelled && (
                    <div>
                      {/* Edit options header with info toggle */}
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-[#999] uppercase tracking-wider">Edit Options</p>
                        <button
                          onClick={() => setEditInfoOpen(editInfoOpen === website.id ? null : website.id)}
                          className="flex items-center gap-1.5 text-xs text-[#999] hover:text-[#F5C518] transition-colors cursor-pointer group"
                        >
                          <div className="w-5 h-5 flex items-center justify-center rounded-full border border-[#ddd] group-hover:border-[#F5C518] transition-colors">
                            <i className="ri-question-line text-[11px]"></i>
                          </div>
                          <span className="tracking-wide">Which option is right for me?</span>
                        </button>
                      </div>

                      {/* Info panel */}
                      {editInfoOpen === website.id && (
                        <div className="mb-3 bg-[#FAFAF8] border border-[#eee] rounded-sm px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="flex gap-3">
                            <div className="w-8 h-8 flex items-center justify-center bg-[#F5C518]/10 rounded-sm shrink-0 mt-0.5">
                              <i className="ri-edit-2-line text-sm text-[#F5C518]"></i>
                            </div>
                            <div>
                              <p className="font-display text-xs tracking-widest text-[#1a1a1a] uppercase mb-1">Update Request — $32</p>
                              <p className="text-xs text-[#666] leading-relaxed">
                                Up to <strong className="text-[#1a1a1a]">12 changes</strong> per request: Add a section, update text, delete something — each individually count as 1 edit. <strong className="text-[#1a1a1a]">Unused edits don&apos;t roll over.</strong>
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <div className="w-8 h-8 flex items-center justify-center bg-[#F5C518]/10 rounded-sm shrink-0 mt-0.5">
                              <i className="ri-scissors-cut-line text-sm text-[#F5C518]"></i>
                            </div>
                            <div>
                              <p className="font-display text-xs tracking-widest text-[#1a1a1a] uppercase mb-1">Single Edit — $5</p>
                              <p className="text-xs text-[#666] leading-relaxed">
                                Only need a change or two? Pay <strong className="text-[#1a1a1a]">$5 per edit</strong> .
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <a
                        href="https://form.jotform.com/260678682211056"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white border border-[#eee] hover:border-[#F5C518] px-5 py-4 rounded-sm transition-all cursor-pointer group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center bg-[#F5C518]/10 rounded-sm shrink-0 group-hover:bg-[#F5C518]/20 transition-colors">
                          <i className="ri-edit-2-line text-base text-[#F5C518]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display text-[#1a1a1a] text-sm tracking-wide">UPDATE REQUEST</p>
                          <p className="text-xs text-[#999]">$32.00 per request</p>
                        </div>
                        <i className="ri-arrow-right-line text-[#ddd] group-hover:text-[#F5C518] transition-colors shrink-0"></i>
                      </a>

                      <a
                        href="https://buy.stripe.com/fZudR23RrbYScDg78NfEk09"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white border border-[#eee] hover:border-[#F5C518] px-5 py-4 rounded-sm transition-all cursor-pointer group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center bg-[#F5C518]/10 rounded-sm shrink-0 group-hover:bg-[#F5C518]/20 transition-colors">
                          <i className="ri-scissors-cut-line text-base text-[#F5C518]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display text-[#1a1a1a] text-sm tracking-wide">SINGLE EDIT</p>
                          <p className="text-xs text-[#999]">$5.00 per edit</p>
                        </div>
                        <i className="ri-arrow-right-line text-[#ddd] group-hover:text-[#F5C518] transition-colors shrink-0"></i>
                      </a>

                      {website.stripePortalUrl ? (
                        <a
                          href={website.stripePortalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 bg-white border border-[#eee] hover:border-red-200 px-5 py-4 rounded-sm transition-all cursor-pointer group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-sm shrink-0 group-hover:bg-red-100 transition-colors">
                            <i className="ri-close-circle-line text-base text-red-400"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-display text-[#1a1a1a] text-sm tracking-wide">CANCEL SUBSCRIPTION</p>
                            <p className="text-xs text-[#999]">Manage billing &amp; cancel</p>
                          </div>
                          <i className="ri-arrow-right-line text-[#ddd] group-hover:text-red-300 transition-colors shrink-0"></i>
                        </a>
                      ) : (
                        <button
                          onClick={() => openCancelModal(website.id)}
                          className="flex items-center gap-3 bg-white border border-[#eee] hover:border-red-200 px-5 py-4 rounded-sm transition-all cursor-pointer group text-left"
                        >
                          <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-sm shrink-0 group-hover:bg-red-100 transition-colors">
                            <i className="ri-close-circle-line text-base text-red-400"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-display text-[#1a1a1a] text-sm tracking-wide">CANCEL SUBSCRIPTION</p>
                            <p className="text-xs text-[#999]">Submit a cancellation request</p>
                          </div>
                          <i className="ri-arrow-right-line text-[#ddd] group-hover:text-red-300 transition-colors shrink-0"></i>
                        </button>
                      )}
                    </div>
                    </div>
                  )}

                  {isCancelled && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-100 px-5 py-4 rounded-sm">
                      <i className="ri-information-line text-red-400 shrink-0"></i>
                      <p className="text-sm text-red-500">
                        Cancellation request submitted. Your site stays live until the end of your billing period.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#FAFAF8] border-t border-[#eee]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-display text-sm tracking-[0.1em] text-[#1a1a1a]">YASMINE CREATES</span>
          <p className="text-xs text-[#999]">&copy; 2025 Yasmine Creates. All rights reserved.</p>
        </div>
      </footer>

      {/* Cancel Modal */}
      {cancelModal && cancellingWebsite && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-sm w-full max-w-lg border border-[#eee]">
            <div className="flex items-center justify-between px-8 py-6 border-b border-[#eee]">
              <div>
                <h3 className="font-display text-[#1a1a1a] text-xl tracking-wide">CANCEL SUBSCRIPTION</h3>
                <p className="text-xs text-[#999] mt-0.5">
                  {cancellingWebsite.websiteLink || cancellingWebsite.businessOwner || 'Website'}
                </p>
              </div>
              <button
                onClick={closeCancelModal}
                className="w-8 h-8 flex items-center justify-center text-[#999] hover:text-[#1a1a1a] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {cancelSuccess ? (
              <div className="px-8 py-12 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-2xl text-red-400"></i>
                </div>
                <p className="font-display text-[#1a1a1a] text-lg tracking-wide mb-2">REQUEST RECEIVED</p>
                <p className="text-sm text-[#666]">
                  Your cancellation has been submitted. Your site stays live until the end of your billing period.
                </p>
              </div>
            ) : (
              <form onSubmit={(e) => handleCancelSubmit(e, cancellingWebsite)} className="px-8 py-6 space-y-5">
                <div className="bg-red-50 border border-red-100 px-4 py-4 rounded-sm">
                  <p className="text-sm text-red-500 leading-relaxed">
                    <strong>Heads up:</strong> Cancelling will take your website offline at the end of your current billing period.
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-[#666] mb-2">
                    Reason for cancelling <span className="text-[#bbb] text-xs">(optional)</span>
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value.slice(0, 500))}
                    placeholder="Let me know why you're leaving..."
                    rows={4}
                    className="w-full px-4 py-3 border border-[#ddd] rounded-sm text-sm focus:outline-none focus:border-[#F5C518] transition-colors resize-none"
                  />
                  <p className="text-xs text-[#bbb] mt-1 text-right">{cancelReason.length}/500</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeCancelModal}
                    className="flex-1 py-3 border border-[#ddd] rounded-sm text-sm text-[#666] hover:border-[#999] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Keep My Plan
                  </button>
                  <button
                    type="submit"
                    disabled={cancelSubmitting}
                    className={`flex-1 py-3 rounded-sm text-sm font-medium tracking-wide transition-all whitespace-nowrap ${
                      cancelSubmitting
                        ? 'bg-[#eee] text-[#bbb] cursor-not-allowed'
                        : 'bg-red-500 text-white hover:bg-red-600 cursor-pointer'
                    }`}
                  >
                    {cancelSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Processing...
                      </span>
                    ) : (
                      'Yes, Cancel'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
